/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ISeparatorProps, Separator } from '@fluentui/react/lib/Separator';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import 'rc-slider/assets/index.css';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { PageSize } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageUIConfigStore';
import { Filters } from 'src/luis/projects/utterances/types/Filters';
import { FiltersBladeSlider } from 'src/verticals/utterancesFilters/components/FiltersBladeSlider';
import { FiltersSection } from 'src/verticals/utterancesFilters/components/FiltersSection';
import { DisplayedFilters } from 'src/verticals/utterancesFilters/FiltersBlade';
import { FilterType, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type MainFiltersBladeProps = {
    /**
     * Count the number of filtered utterances
     */
    resultsCount: number;

    /**
     * Filter types/controls to display in the filters blade.
     */
    displayedFilters: DisplayedFilters;

    /**
     * Object that contain the different types of filters
     */
    filters: Filters;

    /**
     * The size of the utterances page.
     */
    pageSize: PageSize;

    /**
     * Handler function for items selection
     */
    onChangePageSize: (size: PageSize) => void;

    /**
     * Handler function for entities and nearest rival blade
     */
    onOpenBladeClicked: (bladeId: FilterType) => void;

    /**
     * Handler function for item selection
     */
    onSelectionChange: (change: SelectionChange) => void;
};

const messages = defineMessages({
    scoreHeader: {
        id: 'utterancesFilters.mainFiltersBlade.scoreHeader',
        defaultMessage: 'Intent score'
    },
    entitiesHeader: {
        id: 'utterancesFilters.mainFiltersBlade.entitiesHeader',
        defaultMessage: 'Entities'
    },
    nearestIntentHeader: {
        id: 'utterancesFilters.mainFiltersBlade.nearestIntentHeader',
        defaultMessage: 'Nearest intent'
    },
    intentsHeader: {
        id: 'utterancesFilters.mainFiltersBlade.intentsHeader',
        defaultMessage: 'Intents'
    },
    differenceHeader: {
        id: 'utterancesFilters.mainFiltersBlade.differenceHeader',
        defaultMessage: 'Difference'
    },
    scoreSliderMinAriaLabel: {
        id: 'utterancesFilters.mainFiltersBlade.scoreSliderMinAriaLabel',
        defaultMessage: 'minimum score'
    },
    scoreSliderMaxAriaLabel: {
        id: 'utterancesFilters.mainFiltersBlade.scoreSliderMaxAriaLabel',
        defaultMessage: 'maximum score'
    },
    paginationSettingButtonDescription: {
        id: 'utterancesFilters.mainFiltersBlade.paginationSettingButtonDescription',
        defaultMessage: '{count} utterances per page'
    },
    viewPerPage: {
        id: 'utterancesFilters.mainFiltersBlade.viewPerPage',
        defaultMessage: 'View per page'
    },
    resultsCount: {
        id: 'utterancesFilters.mainFiltersBlade.resultsCount',
        defaultMessage: '{resultsCount} results'
    }
});

const theme = getScopedTheme('MainFiltersBlade');
const { styleComponent } = getStylistV2('MainFiltersBlade');

const StyledSeparator = styleComponent<ISeparatorProps>(Separator)('StyledSeparator', {
    height: 14
});

const FiltersWrapper = styleComponent<IStackProps>(Stack)('FiltersWrapper', {
    marginTop: 26
});

const ResultsCount = styleComponent(LuisText)('ResultsCount', {
    marginBottom: 8
});

const pageSizeBtnStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { height: theme.pageSizeBtnHeight }
});

const unselectedPageSizeBtnStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { color: theme.primaryColor }
});

/**
 * Represents the main filters blade.
 */
export const MainFiltersBlade = (props: MainFiltersBladeProps) => {
    const { filters, onOpenBladeClicked, onSelectionChange, pageSize, onChangePageSize, resultsCount, displayedFilters } = props;
    const { localize } = useLocalization();
    const [renderSlider, setRenderSlider] = React.useState(false);

    React.useEffect(() => {
        /**
         * To get the final positions of the slider handles after
         * the blade animation finished and set the handles tooltip positions
         */
        setTimeout(() => setRenderSlider(true), 100);
    }, []);

    return (
        <>
            <Stack tokens={{ childrenGap: 16 }}>
                <ResultsCount>{localize(messages.resultsCount, { resultsCount })}</ResultsCount>

                {displayedFilters.includes('score') && (
                    <>
                        <LuisText fontWeight={600} color={theme.titlesColor}>
                            {localize(messages.scoreHeader)}
                        </LuisText>
                        {renderSlider && (
                            <FiltersBladeSlider
                                score={filters.score}
                                onScoreChange={onSelectionChange}
                                minHandleAriaLabel={localize(messages.scoreSliderMinAriaLabel)}
                                maxHandleAriaLabel={localize(messages.scoreSliderMaxAriaLabel)}
                            />
                        )}
                    </>
                )}
            </Stack>

            <FiltersWrapper tokens={{ childrenGap: 24 }}>
                {displayedFilters.includes('entities') && (
                    <FiltersSection
                        title={localize(messages.entitiesHeader)}
                        onSelectionChange={onSelectionChange}
                        items={filters.entities}
                        itemsLimit={3}
                        onSeeAllBtnClick={() => onOpenBladeClicked('entities')}
                    />
                )}

                <FiltersSection
                    title={localize(displayedFilters.includes('intents') ? messages.intentsHeader : messages.nearestIntentHeader)}
                    onSelectionChange={onSelectionChange}
                    items={filters.nearestRivals}
                    itemsLimit={3}
                    onSeeAllBtnClick={() => onOpenBladeClicked('intents')}
                />

                {displayedFilters.includes('difference') && (
                    <FiltersSection
                        title={localize(messages.differenceHeader)}
                        onSelectionChange={onSelectionChange}
                        items={filters.difference}
                    />
                )}

                {displayedFilters.includes('pageCount') && (
                    <Stack tokens={{ childrenGap: 16 }}>
                        <LuisText fontWeight={600} color={theme.titlesColor}>
                            {localize(messages.viewPerPage)}
                        </LuisText>

                        <Stack horizontal>
                            {([10, 20, 50] as const).map(size => (
                                <React.Fragment key={`size-${size}`}>
                                    <ActionButton
                                        onClick={() => onChangePageSize(size)}
                                        ariaLabel={localize(messages.paginationSettingButtonDescription, { count: size })}
                                        styles={{
                                            root: mergeStyles(pageSizeBtnStyles.root, pageSize !== size && unselectedPageSizeBtnStyles.root)
                                        }}>
                                        {size}
                                    </ActionButton>
                                    <StyledSeparator vertical />
                                </React.Fragment>
                            ))}
                        </Stack>
                    </Stack>
                )}
            </FiltersWrapper>
        </>
    );
};
